// Lightweight rule-based "AI" scoring over the live sensor state.
// Returns a risk score 0..100, a level, and a list of human-readable findings
// with recommended actions. Replace with a model API when one is available.

export function computeRisk(s) {
  const findings = [];
  let score = 0;

  // Temperature
  if (s.temperature > 45) {
    score += 35; findings.push({
      severity: 'critical',
      title: 'Critical overheating',
      detail: `Temperature is ${s.temperature.toFixed(1)}°C — well above safe ceiling.`,
      action: 'Activate cooling / open ventilation immediately.'
    });
  } else if (s.temperature > 38) {
    score += 18; findings.push({
      severity: 'warning',
      title: 'Elevated temperature',
      detail: `Temperature at ${s.temperature.toFixed(1)}°C trending above optimal range.`,
      action: 'Check HVAC; verify door seals.'
    });
  } else if (s.temperature < 2 && s.temperature !== 0) {
    score += 22; findings.push({
      severity: 'warning',
      title: 'Low temperature',
      detail: `${s.temperature.toFixed(1)}°C — risk of freeze damage.`,
      action: 'Enable heater; insulate sensitive cargo.'
    });
  }

  // Humidity
  if (s.humidity > 85) {
    score += 18; findings.push({
      severity: 'warning',
      title: 'High humidity',
      detail: `${s.humidity.toFixed(0)}% — condensation/mould risk.`,
      action: 'Run dehumidifier; inspect packaging.'
    });
  } else if (s.humidity < 25 && s.humidity > 0) {
    score += 10; findings.push({
      severity: 'info',
      title: 'Very dry environment',
      detail: `${s.humidity.toFixed(0)}% — static and dehydration risk.`,
      action: 'Add humidification if cargo is sensitive.'
    });
  }

  // Vibration (accel magnitude)
  if (s.vibration > 15) {
    score += 25; findings.push({
      severity: 'critical',
      title: 'Severe shock detected',
      detail: `${s.vibration.toFixed(2)} m/s² spike — impact event.`,
      action: 'Inspect cargo for damage; review handling.'
    });
  } else if (s.vibration > 11.5) {
    score += 12; findings.push({
      severity: 'warning',
      title: 'High vibration',
      detail: `Sustained ${s.vibration.toFixed(2)} m/s² above baseline.`,
      action: 'Re-secure straps; check mounting.'
    });
  }

  // Gas
  if (s.gas > 1.5) {
    score += 30; findings.push({
      severity: 'critical',
      title: 'Hazardous gas concentration',
      detail: `${s.gas.toFixed(2)} mg/m³ — well above safe threshold.`,
      action: 'Evacuate, ventilate, isolate source.'
    });
  } else if (s.gas > 0.8) {
    score += 12; findings.push({
      severity: 'warning',
      title: 'Elevated gas reading',
      detail: `${s.gas.toFixed(2)} mg/m³ trending up.`,
      action: 'Increase airflow; verify cargo manifest.'
    });
  }

  // Battery
  if (s.battery < 15) {
    score += 20; findings.push({
      severity: 'critical',
      title: 'Battery critically low',
      detail: `${s.battery.toFixed(0)}% remaining.`,
      action: 'Connect charger or replace power pack.'
    });
  } else if (s.battery < 30) {
    score += 6; findings.push({
      severity: 'info',
      title: 'Low battery',
      detail: `${s.battery.toFixed(0)}% — schedule charging.`,
      action: 'Plan charge cycle within the next hour.'
    });
  }

  // Motion at rest can indicate tampering
  if (s.motion === 1 && s.vibration < 10) {
    score += 8; findings.push({
      severity: 'info',
      title: 'Unexpected motion',
      detail: 'Movement detected while container appears at rest.',
      action: 'Cross-check with GPS/route; verify access logs.'
    });
  }

  if (findings.length === 0) {
    findings.push({
      severity: 'ok',
      title: 'All systems nominal',
      detail: 'Every monitored channel is within safe limits.',
      action: 'No action required.'
    });
  }

  score = Math.min(100, Math.round(score));
  const level =
    score >= 70 ? 'critical' :
    score >= 40 ? 'warning'  :
    score >= 15 ? 'caution'  : 'safe';

  return { score, level, findings };
}

export function levelColor(level) {
  switch (level) {
    case 'critical': return { fg: '#ef4444', bg: 'rgba(239,68,68,0.10)', ring: 'ring-red-500/30' };
    case 'warning':  return { fg: '#f59e0b', bg: 'rgba(245,158,11,0.10)', ring: 'ring-amber-500/30' };
    case 'caution':  return { fg: '#3b82f6', bg: 'rgba(59,130,246,0.10)', ring: 'ring-blue-500/30' };
    default:         return { fg: '#10b981', bg: 'rgba(16,185,129,0.10)', ring: 'ring-emerald-500/30' };
  }
}

export function statusFor(metric, value) {
  switch (metric) {
    case 'temperature':
      if (value > 45 || value < 2) return 'bad';
      if (value > 38)              return 'warn';
      return 'ok';
    case 'humidity':
      if (value > 85 || value < 25) return 'warn';
      return 'ok';
    case 'vibration':
      if (value > 15)   return 'bad';
      if (value > 11.5) return 'warn';
      return 'ok';
    case 'gas':
      if (value > 1.5) return 'bad';
      if (value > 0.8) return 'warn';
      return 'ok';
    case 'battery':
      if (value < 15) return 'bad';
      if (value < 30) return 'warn';
      return 'ok';
    case 'motion':
      return value ? 'warn' : 'ok';
    default:
      return 'ok';
  }
}
